/**
 * AuthCallbackPage - Google OAuth Callback Handler
 * =================================================
 * SESSION 109: SaaS Auth
 * 
 * This page handles the redirect from Google OAuth.
 * URL: /auth/callback?token=<jwt>
 * 
 * Flow:
 * 1. Extract token from URL query params
 * 2. Call loginWithToken() from AuthContext
 * 3. Redirect to dashboard on success, login on failure
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken, isAuthenticated } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleAuth = async () => {
      const token = searchParams.get('token');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        // Redirect to login with error
        navigate(`/login?error=${errorParam}`, { replace: true });
        return;
      }

      if (!token) {
        setError('No authentication token received');
        setTimeout(() => navigate('/login?error=no_token', { replace: true }), 2000);
        return;
      }

      // Attempt to login with the token (now async)
      const success = await loginWithToken(token);
      
      if (success) {
        // Success - redirect to dashboard
        navigate('/', { replace: true });
      } else {
        // Failed - redirect to login with error
        setError('Invalid or expired token');
        setTimeout(() => navigate('/login?error=invalid_token', { replace: true }), 2000);
      }
    };

    handleAuth();
  }, [searchParams, loginWithToken, navigate]);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center" 
      style={{ background: '#0a0a0f' }}
      data-testid="auth-callback-page"
    >
      <div className="text-center">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-400 text-sm mb-2">{error}</p>
            <p className="text-gray-500 text-xs">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Completing sign in...</p>
            <p className="text-gray-600 text-xs mt-1">Please wait</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
